import { createContext, useCallback, useContext } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { encodeObjectForWaf } from 'src/lib/wafEncoding'
import { INTAKE_NOTE_TYPES } from 'src/participantHistory/constants'
import { GetAdvancedCaseloadParticipantsDocument } from '../app/advancedCaseload.generated'
import { useAddAdvancedFinancialEligibilityPatientHistoryMutation } from './addAdvancedFinancialEligibilityPatientHistory.generated'
import { useAddAdvancedPatientHistoryEntryMutation } from './addAdvancedPatientHistoryEntry.generated'
import { GetAdvancedPatientHistoryDocument } from './advancedPatientHistory.generated'
import { GetAdvancedPatientHistoryFormTypesDocument } from './advancedPatientHistoryFormTypes.generated'
import { GetAdvancedUncancelledInProgramCallsDocument } from './advancedUncancelledInProgramCalls.generated'
import { clearHistoryDraft } from './historyDraft'
import { HOME_VIEW_CASELOAD } from './homeViewCaseload'

export type NewPatientHistoryEntry = {
  date: string
  type: PatientHistoryType
  value: Record<string, unknown>
  fileIDs?: string[]
  references?: { callID: string }
}

// Only the server knows whether another completed call still awaits a note, so the caseload lists refetch.
const clearsCallFollowUp = ({ type, references }: NewPatientHistoryEntry) =>
  Boolean(references) ||
  (INTAKE_NOTE_TYPES as readonly PatientHistoryType[]).includes(type) ||
  type === PatientHistoryType.MissedIntakeCall

const refetchesFor = (uid: string, entry: NewPatientHistoryEntry) => [
  { query: GetAdvancedPatientHistoryDocument, variables: { uid } },
  { query: GetAdvancedPatientHistoryFormTypesDocument, variables: { uid } },
  ...(entry.references ? [{ query: GetAdvancedUncancelledInProgramCallsDocument, variables: { uid } }] : []),
  ...(clearsCallFollowUp(entry) ? [HOME_VIEW_CASELOAD] : []),
  ...(clearsCallFollowUp(entry) || entry.type === PatientHistoryType.MonthlySummaryNote
    ? [GetAdvancedCaseloadParticipantsDocument]
    : []),
]

const EntryAddedContext = createContext<() => void>(() => undefined)

// The logger closes a form once its entry is saved, so a second click cannot add it twice.
export const EntryAddedProvider = EntryAddedContext.Provider

type SubmitOptions = { keepFormOpen?: boolean }

export const useSubmitPatientHistory = (uid: string) => {
  const [mutate, { loading, error }] = useAddAdvancedPatientHistoryEntryMutation()
  const onAdded = useContext(EntryAddedContext)

  const submit = useCallback(
    async (entry: NewPatientHistoryEntry, { keepFormOpen = false }: SubmitOptions = {}) => {
      const result = await mutate({
        // Cloud Armor can reject a note holding patterns like `<script>`; the server decodes by the marker.
        variables: { uid, ...entry, value: encodeObjectForWaf(entry.value) },
        refetchQueries: refetchesFor(uid, entry),
      })

      clearHistoryDraft(uid, entry.type)

      if (!keepFormOpen) {
        onAdded()
      }

      return result
    },
    [mutate, uid, onAdded]
  )

  return { submit, submitting: loading, error }
}

export type FinancialEligibilityHealthPlanEntry = {
  date: string
  description: string
  costEstimate?: {
    intakeCostEstimateInCents: number
    month1CostEstimateInCents: number
    month2CostEstimateInCents: number
    month3CostEstimateInCents: number
  }
  insuranceSubscriber?: {
    firstname: string
    lastname: string
    memberID: string
    coverageActive: boolean
    groupNumber?: string
    groupDescription?: string
    birthDate?: string
  }
}

// A dedicated mutation, but it still lands as a `FINANCIAL_ELIGIBILITY_CONFIRMED` entry.
export const useSubmitFinancialEligibilityHealthPlan = (uid: string) => {
  const [mutate, { loading, error }] = useAddAdvancedFinancialEligibilityPatientHistoryMutation({
    refetchQueries: [{ query: GetAdvancedPatientHistoryDocument, variables: { uid } }],
  })

  const onAdded = useContext(EntryAddedContext)

  const submit = useCallback(
    async (entry: FinancialEligibilityHealthPlanEntry) => {
      const result = await mutate({ variables: { uid, ...entry } })

      onAdded()

      return result
    },
    [mutate, uid, onAdded]
  )

  return { submit, submitting: loading, error }
}
